import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCoins, faArrowDown, faArrowUp, faChartBar, faUniversity,
  faSearch, faXmark, faBan, faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';

dayjs.extend(utc);
dayjs.extend(timezone);

const BKK = 'Asia/Bangkok';
const getFirstDayOfMonth = () => dayjs().tz(BKK).startOf('month').format('YYYY-MM-DD');
const getTodayStr        = () => dayjs().tz(BKK).format('YYYY-MM-DD');

const fmt = (n: number) =>
  n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

type StatusFilter = 'all' | 'active' | 'inactive';

interface AgentCommissionRow {
  id: string;
  name: string;
  email: string;
  deposit_commission_rate: number;
  withdrawal_commission_rate: number;
  is_active: boolean;
  bank_count: number;
  total_deposit: number;
  total_withdrawal: number;
  deposit_commission: number;
  withdrawal_commission: number;
  total_commission: number;
}

interface OverviewSummary {
  total_deposit: number;
  total_withdrawal: number;
  total_deposit_commission: number;
  total_withdrawal_commission: number;
  total_commission: number;
}

const emptySummary: OverviewSummary = {
  total_deposit: 0, total_withdrawal: 0,
  total_deposit_commission: 0, total_withdrawal_commission: 0, total_commission: 0,
};

const AgentCommissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AgentCommissionRow[]>([]);
  const [summary, setSummary] = useState<OverviewSummary>(emptySummary);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(getFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getTodayStr());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || 'null');
  const isAdminUser = adminUser?.type === 'admin' || !adminUser?.type;

  // seq กัน response เก่าที่มาช้าทับผลของ search/reset ที่ใหม่กว่า
  const fetchSeq = useRef(0);

  const fetchData = useCallback(async (from: string, to: string) => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const res = await merchantApi.get('/admin/agent-banks/commissions', {
        params: { date_from: from, date_to: to },
      });
      if (seq !== fetchSeq.current) return;
      if (res.data.success) {
        setRows(res.data.data.agents);
        setSummary(res.data.data.summary);
      }
    } catch {
      if (seq === fetchSeq.current) toast.error('ดึงข้อมูลค่าคอมมิชชั่นไม่สำเร็จ');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(getFirstDayOfMonth(), getTodayStr());
  }, [fetchData]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด');
      return;
    }
    fetchData(dateFrom, dateTo);
  };

  const handleReset = () => {
    const f = getFirstDayOfMonth();
    const t = getTodayStr();
    setDateFrom(f);
    setDateTo(t);
    setSearchQuery('');
    setStatusFilter('all');
    fetchData(f, t);
  };

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter === 'active' && !r.is_active) return false;
      if (statusFilter === 'inactive' && r.is_active) return false;
      if (!q) return true;
      return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
    });
  }, [rows, searchQuery, statusFilter]);

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faArrowDown,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'ยอดฝากรวม',
      value: <span className="text-green-600 dark:text-green-400">฿{fmt(summary.total_deposit)}</span>,
    },
    {
      icon: faArrowUp,
      iconClassName: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'ยอดถอนรวม',
      value: <span className="text-amber-600 dark:text-amber-400">฿{fmt(summary.total_withdrawal)}</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'ค่าคอมฝาก',
      value: <span className="text-blue-600 dark:text-blue-400">฿{fmt(summary.total_deposit_commission)}</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: 'ค่าคอมถอน',
      value: <span className="text-purple-600 dark:text-purple-400">฿{fmt(summary.total_withdrawal_commission)}</span>,
    },
    {
      icon: faChartBar,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'รวมค่าคอมทั้งหมด',
      value: <span style={{ color: 'var(--theme-primary)' }}>฿{fmt(summary.total_commission)}</span>,
    },
  ], [summary]);

  const columns: Column<AgentCommissionRow>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Agent',
      sortable: true,
      render: (r) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--theme-primary-light)' }}
          >
            <FontAwesomeIcon icon={faUniversity} className="text-xs" style={{ color: 'var(--theme-primary)' }} />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-gray-800 dark:text-slate-100 truncate">
              {r.name}
              {!r.is_active && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">ปิดใช้</span>
              )}
            </p>
            <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">{r.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'bank_count',
      header: 'ธนาคาร',
      sortable: true,
      width: '80px',
      render: (r) => (
        <span className="text-[12px] text-gray-600 dark:text-slate-300">{r.bank_count} บัญชี</span>
      ),
    },
    {
      key: 'rates',
      header: 'เรท ฝาก/ถอน',
      width: '120px',
      render: (r) => (
        <div className="flex items-center gap-1">
          <span className="px-1.5 py-0.5 rounded-md text-[11px] font-semibold bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400">
            {r.deposit_commission_rate}%
          </span>
          <span className="px-1.5 py-0.5 rounded-md text-[11px] font-semibold bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
            {r.withdrawal_commission_rate}%
          </span>
        </div>
      ),
    },
    {
      key: 'total_deposit',
      header: 'ยอดฝากรวม',
      sortable: true,
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => <span className="text-[13px] font-medium text-green-600 dark:text-green-400">฿{fmt(r.total_deposit)}</span>,
    },
    {
      key: 'deposit_commission',
      header: 'ค่าคอมฝาก',
      sortable: true,
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => <span className="text-[13px] font-semibold text-green-700 dark:text-green-300">฿{fmt(r.deposit_commission)}</span>,
    },
    {
      key: 'total_withdrawal',
      header: 'ยอดถอนรวม',
      sortable: true,
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => <span className="text-[13px] font-medium text-amber-600 dark:text-amber-400">฿{fmt(r.total_withdrawal)}</span>,
    },
    {
      key: 'withdrawal_commission',
      header: 'ค่าคอมถอน',
      sortable: true,
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => <span className="text-[13px] font-semibold text-amber-700 dark:text-amber-300">฿{fmt(r.withdrawal_commission)}</span>,
    },
    {
      key: 'total_commission',
      header: 'รวมค่าคอม',
      sortable: true,
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => (
        <span className="text-[13px] font-bold" style={{ color: 'var(--theme-primary)' }}>฿{fmt(r.total_commission)}</span>
      ),
    },
    {
      key: 'detail',
      header: '',
      width: '40px',
      render: () => (
        <FontAwesomeIcon icon={faChevronRight} className="text-gray-300 dark:text-slate-600 text-xs" />
      ),
    },
  ], []);

  if (!isAdminUser) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-slate-400">
        <FontAwesomeIcon icon={faBan} className="text-4xl text-red-300 dark:text-red-500/50 mb-3" />
        <p>ไม่มีสิทธิ์เข้าถึงหน้านี้ — เฉพาะ Admin เท่านั้น</p>
      </div>
    );
  }

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faCoins}
        title="ค่าคอม Agent"
        subtitle={`สรุปค่าคอมมิชชั่นของ Agent Bank ทุกราย • คลิกแถวเพื่อดูรายละเอียดและธุรกรรม`}
      />

      <UiFilterPanel
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
      >
        <div className="w-[240px] shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            ค้นหา
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ค้นหาชื่อ, อีเมล..."
              className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:ring-slate-600 dark:focus:border-slate-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
              >
                <FontAwesomeIcon icon={faXmark} className="text-sm" />
              </button>
            )}
          </div>
        </div>

        <div className="w-[150px] shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="all">ทั้งหมด</option>
            <option value="active">ใช้งาน</option>
            <option value="inactive">ปิดใช้</option>
          </select>
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statCards} loading={loading} cols={5} />

      <DataTable
        key={`${searchQuery}|${statusFilter}`} // filter เปลี่ยน → reset หน้า pagination ภายใน
        data={filteredRows}
        columns={columns}
        loading={loading}
        keyExtractor={(r) => r.id}
        emptyMessage={searchQuery ? 'ไม่พบ Agent ที่ค้นหา' : 'ยังไม่มี Agent Bank'}
        emptyIcon={<FontAwesomeIcon icon={faCoins} className="text-5xl text-gray-200 dark:text-slate-600" />}
        onRowClick={(r) => navigate(`/admin/agent-commissions/${r.id}`)}
        hoverable
        compact
        pagination={{
          enabled: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          showPageSizeSelector: true,
          showInfo: true,
          showFirstLast: true,
        }}
      />
    </div>
  );
};

export default AgentCommissionsPage;
